import React, { useEffect, useRef, useState } from "react";
import { getLang, t, fmtNum } from "./i18n";
import { navigate, useStore } from "./store";

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

export function Link({ to, children, className, onClick }: { to: string; children: React.ReactNode; className?: string; onClick?: () => void }) {
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onClick?.();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

export function Button({ kind = "primary", busy, children, className, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: "primary" | "ghost" | "danger" | "quiet"; busy?: boolean }) {
  return (
    <button {...rest} className={cx("btn", `btn-${kind}`, busy && "is-busy", className)} disabled={rest.disabled || busy}>
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <label className={cx("field", error && "has-error")}>
      <span className="field-label">{label}</span>
      {children}
      {error ? <span className="field-error">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    ref.current?.querySelector<HTMLElement>("input,button,select,textarea")?.focus();
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={cx("modal", wide && "modal-wide")} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t.close}>
            <svg viewBox="0 0 24 24" width="20" height="20"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export function Toasts() {
  const toasts = useStore((s) => s.toasts);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((x) => (
        <div key={x.id} className={cx("toast", `toast-${x.kind}`)}>
          {x.text}
        </div>
      ))}
    </div>
  );
}

/** Microphone glyph (SVG, not emoji, so it renders the same in every font). */
export function MicIcon({ state, size = 14 }: { state: "on" | "off" | "server"; size?: number }) {
  return (
    <svg className={cx("mic-icon", `mic-${state}`)} viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
      {state !== "on" && <path d="M3 3l18 18" />}
    </svg>
  );
}

export function Spinner() {
  return <div className="spinner" aria-label={t.loading} />;
}

export function Coin({ size = 18 }: { size?: number }) {
  return (
    <svg className="coin" viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#F7B32B" />
      <circle cx="12" cy="12" r="7" fill="none" stroke="#FFD36E" strokeWidth="1.5" />
      <path d="M9.5 13.5a3.5 3.5 0 1 0 2.5-6 3 3 0 0 1 0 6z" fill="#C98400" />
    </svg>
  );
}

export function Coins({ n }: { n: number }) {
  return (
    <span className="coins">
      <Coin /> <bdi>{fmtNum(n)}</bdi>
    </span>
  );
}

const AV_COLORS = ["#FF6B5B", "#2ED3B7", "#F7B32B", "#8C7CFF", "#4FA3FF", "#FF8FB1"];
export function Avatar({ name, url, size = 40, online, frame }: { name: string; url?: string | null; size?: number; online?: boolean; frame?: string | null }) {
  const h = [...(name || "?")].reduce((a, c) => a + c.charCodeAt(0), 0);
  const bg = AV_COLORS[h % AV_COLORS.length];
  const art = url?.startsWith("avatar:") ? AVATAR_ART[url.slice(7)] : null;
  return (
    <span className={cx("avatar", frame && `frame-${frame}`)} style={{ width: size, height: size }}>
      {art ? (
        <svg viewBox="0 0 64 64" width={size} height={size}>{art}</svg>
      ) : (
        <span className="avatar-initial" style={{ background: bg, fontSize: size * 0.42 }}>
          {[...(name || "?")][0]?.toUpperCase()}
        </span>
      )}
      {online !== undefined && <span className={cx("presence", online && "is-online")} aria-label={online ? t.online : t.offline} />}
    </span>
  );
}

export const AVATAR_CHOICES = ["falcon", "camel", "palm", "lantern", "moon", "star"];
const AVATAR_ART: Record<string, React.ReactNode> = {
  falcon: (<><rect width="64" height="64" fill="#2E2275" /><path d="M14 40c10-2 18-10 22-22 4 6 4 14 0 20l12 4-14 4c-6 4-14 4-20-6z" fill="#F7B32B" /><circle cx="36" cy="24" r="2" fill="#1A1240" /></>),
  camel: (<><rect width="64" height="64" fill="#FFB86B" /><path d="M10 46h44M18 46V34c0-6 6-10 10-6 2-6 10-6 12 0 4 0 6 4 6 8v10M46 30l6-6" stroke="#5B2E10" strokeWidth="4" fill="none" strokeLinecap="round" /></>),
  palm: (<><rect width="64" height="64" fill="#2ED3B7" /><path d="M32 54V26M32 26c-8-8-18-6-20 0M32 26c8-8 18-6 20 0M32 26c-4-10-12-12-16-10M32 26c4-10 12-12 16-10" stroke="#0E4A40" strokeWidth="4" fill="none" strokeLinecap="round" /></>),
  lantern: (<><rect width="64" height="64" fill="#1A1240" /><path d="M24 20h16l4 8v14l-4 8H24l-4-8V28z" fill="#F7B32B" /><path d="M28 12h8v8h-8z" fill="#FFD36E" /><circle cx="32" cy="36" r="5" fill="#FF6B5B" /></>),
  moon: (<><rect width="64" height="64" fill="#2E2275" /><path d="M40 14a20 20 0 1 0 10 30A16 16 0 1 1 40 14z" fill="#FFD36E" /></>),
  star: (<><rect width="64" height="64" fill="#FF6B5B" /><path d="M32 12l5 13 14 1-11 9 4 14-12-8-12 8 4-14-11-9 14-1z" fill="#FFF4E0" /></>),
};

/** Drawn game pieces used as the game tiles on the home table. */
export function GameArt({ id }: { id: string }) {
  switch (id) {
    case "jackaroo":
      return (
        <svg viewBox="0 0 120 90" aria-hidden="true">
          <rect x="22" y="12" width="40" height="58" rx="6" fill="#FFF4E0" transform="rotate(-10 42 41)" />
          <rect x="48" y="14" width="40" height="58" rx="6" fill="#FFF4E0" transform="rotate(8 68 43)" />
          <text x="70" y="50" fontSize="22" fontWeight="700" fill="#FF6B5B" transform="rotate(8 68 43)" textAnchor="middle">J</text>
          <circle cx="96" cy="70" r="8" fill="#2ED3B7" /><circle cx="78" cy="78" r="8" fill="#F7B32B" /><circle cx="28" cy="76" r="8" fill="#FF6B5B" />
        </svg>
      );
    case "ludo":
      return (
        <svg viewBox="0 0 120 90" aria-hidden="true">
          <rect x="34" y="16" width="52" height="52" rx="12" fill="#FFF4E0" transform="rotate(-12 60 42)" />
          {[[47, 30], [60, 42], [73, 54], [47, 54], [73, 30]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="5" fill="#1A1240" transform="rotate(-12 60 42)" />)}
          <path d="M98 74c0-8 4-12 4-18a6 6 0 1 0-12 0c0 6 4 10 4 18z" fill="#2ED3B7" /><path d="M24 74c0-8 4-12 4-18a6 6 0 1 0-12 0c0 6 4 10 4 18z" fill="#FF6B5B" />
        </svg>
      );
    case "domino":
      return (
        <svg viewBox="0 0 120 90" aria-hidden="true">
          <g transform="rotate(-18 60 45)">
            <rect x="20" y="28" width="80" height="38" rx="8" fill="#FFF4E0" />
            <path d="M60 32v30" stroke="#1A1240" strokeWidth="2" />
            {[[32, 38], [32, 56], [48, 38], [48, 56], [40, 47]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="3.6" fill="#1A1240" />)}
            {[[72, 38], [88, 56], [80, 47]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="3.6" fill="#FF6B5B" />)}
          </g>
        </svg>
      );
    case "carrom":
      return (
        <svg viewBox="0 0 120 90" aria-hidden="true">
          <circle cx="60" cy="45" r="30" fill="none" stroke="#FFF4E0" strokeWidth="2" opacity=".5" />
          {[0, 1, 2, 3, 4, 5].map((i) => <circle key={i} cx={60 + 14 * Math.cos(i * 1.047)} cy={45 + 14 * Math.sin(i * 1.047)} r="7" fill={i % 2 ? "#1A1240" : "#FFF4E0"} stroke="#FFF4E0" strokeWidth="1.5" />)}
          <circle cx="60" cy="45" r="7" fill="#FF6B5B" />
          <circle cx="96" cy="76" r="10" fill="#F7B32B" />
        </svg>
      );
    case "billiards":
      return (
        <svg viewBox="0 0 120 90" aria-hidden="true">
          <circle cx="44" cy="46" r="20" fill="#1A1240" stroke="#FFF4E0" strokeWidth="2" />
          <circle cx="44" cy="46" r="9" fill="#FFF4E0" /><text x="44" y="51" fontSize="12" fontWeight="700" textAnchor="middle" fill="#1A1240">8</text>
          <circle cx="82" cy="34" r="12" fill="#F7B32B" /><circle cx="86" cy="66" r="12" fill="#FFF4E0" /><path d="M76 66h20" stroke="#FF6B5B" strokeWidth="8" />
          <path d="M8 86L30 62" stroke="#FFD36E" strokeWidth="5" strokeLinecap="round" />
        </svg>
      );
    case "memory":
      return (
        <svg viewBox="0 0 120 90" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <rect key={i} x={20 + (i % 2) * 44} y={8 + Math.floor(i / 2) * 40} width="36" height="34" rx="6" fill={i === 1 ? "#FFF4E0" : "#2E2275"} stroke="#FFF4E0" strokeWidth="2" />)}
          <path d="M82 18a7 7 0 1 0 5 12 6 6 0 1 1-5-12z" fill="#F7B32B" />
        </svg>
      );
  }
  return null;
}

/** Simple drawn icons for gifts and cosmetics (keys come from the database). */
export function GiftIcon({ icon, size = 48 }: { icon: string; size?: number }) {
  const k = icon.split(":")[1] ?? icon;
  const s: Record<string, React.ReactNode> = {
    gahwa: (<><path d="M18 26h28l-4 20H22z" fill="#FFF4E0" /><path d="M46 30h6a5 5 0 0 1 0 10h-7" stroke="#FFF4E0" strokeWidth="3" fill="none" /><path d="M26 18c0-4 4-4 4-8M34 18c0-4 4-4 4-8" stroke="#F7B32B" strokeWidth="2" fill="none" /></>),
    dates: (<>{[0, 1, 2].map((i) => <ellipse key={i} cx={20 + i * 12} cy={36 - (i % 2) * 6} rx="7" ry="11" fill="#8A4A1E" transform={`rotate(${-20 + i * 20} ${20 + i * 12} 36)`} />)}</>),
    rose: (<><path d="M32 30v24" stroke="#2ED3B7" strokeWidth="3" /><circle cx="32" cy="24" r="11" fill="#FF6B5B" /><path d="M26 22a6 6 0 0 1 12 0" stroke="#C2453A" strokeWidth="2" fill="none" /></>),
    clap: (<><path d="M20 40l10-20 6 4-6 16 12-14 5 4-14 20H24z" fill="#FFD36E" /><path d="M44 14l4-4M50 22l6-2M38 10l1-6" stroke="#FFF4E0" strokeWidth="2" /></>),
    lantern: (<><path d="M24 20h16l4 8v14l-4 8H24l-4-8V28z" fill="#F7B32B" /><path d="M28 12h8v8h-8z" fill="#FFD36E" /><circle cx="32" cy="36" r="5" fill="#FF6B5B" /></>),
    trophy: (<><path d="M20 12h24v10a12 12 0 0 1-24 0z" fill="#F7B32B" /><path d="M28 34h8v8h6v6H22v-6h6z" fill="#FFD36E" /><path d="M20 16h-6a6 6 0 0 0 6 8M44 16h6a6 6 0 0 1-6 8" stroke="#F7B32B" strokeWidth="3" fill="none" /></>),
    falcon: (<><path d="M10 40c10-2 18-10 22-22 4 6 4 14 0 20l14 4-16 4c-6 4-14 4-20-6z" fill="#F7B32B" /><circle cx="32" cy="24" r="2" fill="#1A1240" /></>),
    crown: (<><path d="M12 44l4-24 10 12 6-16 6 16 10-12 4 24z" fill="#F7B32B" /><circle cx="32" cy="16" r="3" fill="#FF6B5B" /><rect x="12" y="44" width="40" height="6" fill="#FFD36E" /></>),
    fireworks: (<>{[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <path key={i} d={`M32 32l${Math.round(18 * Math.cos(i * 0.785))} ${Math.round(18 * Math.sin(i * 0.785))}`} stroke={["#FF6B5B", "#F7B32B", "#2ED3B7"][i % 3]} strokeWidth="3" strokeLinecap="round" />)}<circle cx="32" cy="32" r="4" fill="#FFF4E0" /></>),
    crescent: (<path d="M40 12a20 20 0 1 0 10 30A16 16 0 1 1 40 12z" fill="#FFD36E" />),
    gold: (<circle cx="32" cy="32" r="22" fill="none" stroke="#F7B32B" strokeWidth="6" />),
    night: (<><circle cx="32" cy="32" r="22" fill="none" stroke="#2E2275" strokeWidth="6" /><circle cx="48" cy="18" r="3" fill="#FFD36E" /></>),
    sadu: (<><rect x="10" y="14" width="44" height="36" rx="4" fill="#7A1F1F" />{[0, 1, 2, 3].map((i) => <path key={i} d={`M${14 + i * 10} 32l5-6 5 6-5 6z`} fill="#FFF4E0" />)}</>),
    desert: (<><rect x="10" y="14" width="44" height="36" rx="4" fill="#E8B26A" /><path d="M10 40c10-8 20-8 30-2s10 4 14 2v10H10z" fill="#C98A3E" /></>),
    stars: (<>{[[20, 20], [42, 16], [32, 36], [16, 44], [48, 42]].map(([x, y], i) => <path key={i} d={`M${x} ${y - 5}l1.5 3.5 3.5 1.5-3.5 1.5-1.5 3.5-1.5-3.5-3.5-1.5 3.5-1.5z`} fill="#FFD36E" />)}</>),
  };
  const avatar = icon.startsWith("avatar:") ? AVATAR_ART[k] : null;
  return (
    <svg className="gift-icon" viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
      {avatar ?? s[k] ?? <circle cx="32" cy="32" r="16" fill="#F7B32B" />}
    </svg>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [state, set] = useState<{ data?: T; error?: any; loading: boolean }>({ loading: true });
  const [n, bump] = useState(0);
  useEffect(() => {
    let live = true;
    set((s) => ({ ...s, loading: true }));
    fn().then(
      (data) => live && set({ data, loading: false }),
      (error) => live && set({ error, loading: false }),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return { ...state, reload: () => bump((x) => x + 1) };
}

export const dirSign = () => (getLang() === "ar" ? -1 : 1);
